'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';
import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { PgProduct, PgProductSubComponentProps } from './PgProductTypes';

// 曝光检测组件
const ExposureDetector: React.FC<{
  product: PgProduct;
  productIndex: number;
  onExposure: (product: PgProduct) => void;
}> = React.memo(({ product, onExposure }) => {
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && product.id) {
      onExposure(product);
    }
  }, [inView]);

  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
});

ExposureDetector.displayName = 'ExposureDetector';

/**
 * PC端商品组展示组件
 */
const PCProductGroupItem: React.FC<PgProductSubComponentProps> = React.memo(
  ({ productGroup, groupIndex, onProductClick, onProductExposure, shouldLoadVideo = false }) => {
    const { navigate } = useAppNavigation();

    // 当前显示的商品索引
    const [currentProductIndex, setCurrentProductIndex] = useState(0);
    // 视频引用
    const videoRef = useRef<HTMLVideoElement>(null);
    // Swiper引用
    const carouselSwiperRef = useRef<SwiperType | null>(null);
    // 防循环触发标识
    const isUpdatingFromSwiper = useRef(false);

    // 获取当前商品
    const currentProduct = productGroup[currentProductIndex] || productGroup[0];

    // 判断是否为偶数组（布局切换用）
    const isEvenGroup = groupIndex % 2 === 1;

    // 监听 currentProductIndex 变化，同步轮播位置
    useEffect(() => {
      // 如果是从 Swiper 的 onSlideChange 触发的更新，先重置标识
      if (isUpdatingFromSwiper.current) {
        isUpdatingFromSwiper.current = false;
        return;
      }

      const carouselSwiper = carouselSwiperRef.current;
      if (carouselSwiper && !carouselSwiper.destroyed) {
        if (carouselSwiper.activeIndex !== currentProductIndex) {
          carouselSwiper.slideTo(currentProductIndex, 500);
        }
      }
    }, [currentProductIndex]);

    // 监听当前商品视频变化，只在应该加载时才加载视频
    useEffect(() => {
      const video = videoRef.current;

      if (video && currentProduct?.config_video && shouldLoadVideo) {
        // 设置视频源并加载
        video.src = currentProduct.config_video;
        video.load();
        // 自动播放新视频
        video.play().catch(() => {
          // 处理自动播放失败的情况（某些浏览器策略）
        });
      }
    }, [currentProduct?.config_video, shouldLoadVideo]);

    // 缩略图点击切换
    const handleProductSwitch = (productIndex: number) => {
      if (productIndex === currentProductIndex) return;
      setCurrentProductIndex(productIndex);
    };

    // 商品跳转
    const toProductDetail = (product: PgProduct) => {
      navigate(`/goods-detail/${product.shop_sku}`);
    };

    if (!productGroup || productGroup.length === 0) {
      return null;
    }

    return (
      <div className={`flex items-start ${isEvenGroup ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* 轮播区域 */}
        <div className="w-1/2 relative">
          <Swiper
            modules={[Navigation]}
            slidesPerView={1}
            slidesPerGroup={1}
            spaceBetween={0}
            speed={500}
            navigation={{
              nextEl: `.carousel-next-${groupIndex}`,
              prevEl: `.carousel-prev-${groupIndex}`,
              disabledClass: 'swiper-button-disabled opacity-50',
            }}
            onInit={(swiper) => {
              carouselSwiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              const { activeIndex } = swiper;
              if (activeIndex !== undefined && activeIndex !== currentProductIndex) {
                // 标记这是来自 Swiper 滑动的更新
                isUpdatingFromSwiper.current = true;
                setCurrentProductIndex(activeIndex);
              }
            }}
            className="w-full aspect-square"
          >
            {productGroup.map((product, productIndex) => (
              <SwiperSlide
                key={`product-${product.id}-${productIndex}`}
                className="product-slide relative"
              >
                <Image
                  src={product.config_image}
                  alt={product.short_name}
                  width={600}
                  height={600}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => {
                    onProductClick(product);
                    toProductDetail(product);
                  }}
                />
                {/* 曝光检测组件 */}
                <ExposureDetector
                  product={product}
                  productIndex={productIndex}
                  onExposure={onProductExposure}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* 轮播控制按钮 */}
          {productGroup.length > 1 && (
            <div className="absolute top-1/2 transform -translate-y-1/2 w-full flex justify-between px-[16px] pointer-events-none z-10">
              <div
                className={`carousel-prev carousel-prev-${groupIndex} w-[40px] h-[40px] rounded-full bg-black/30 cursor-pointer flex items-center justify-center pointer-events-auto`}
              >
                <LeftArrowOutlined
                  className={`text-[18px] text-white relative right-[1px] transition-opacity ${
                    currentProductIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'opacity-100'
                  }`}
                />
              </div>
              <div
                className={`carousel-next carousel-next-${groupIndex} w-[40px] h-[40px] rounded-full bg-black/30 cursor-pointer flex items-center justify-center pointer-events-auto hover:scale-110 hover:bg-black/40`}
              >
                <RightArrowOutlined
                  className={`text-[18px] text-white relative left-[1px] transition-opacity ${
                    currentProductIndex === productGroup.length - 1
                      ? 'opacity-50 cursor-not-allowed'
                      : 'opacity-100'
                  }`}
                />
              </div>
            </div>
          )}
        </div>

        {/* 内容区域 */}
        <div className="w-1/2 flex flex-col gap-[48px]">
          {/* 商品图片/视频区域 */}
          <div className="product-image-section">
            <div className="relative w-full aspect-[2/1]">
              {currentProduct?.config_video && (
                <video
                  ref={videoRef}
                  className="block w-full h-full object-cover object-center"
                  loop
                  autoPlay
                  muted
                  playsInline
                  preload="none"
                >
                  {/* 视频源通过JavaScript动态设置 */}
                </video>
              )}
            </div>
          </div>

          {/* 商品信息区域 */}
          <div className="px-[56px] text-white">
            {/* 商品缩略图选择器 */}
            {productGroup.length && (
              <div
                className={`flex gap-[30px] flex-wrap ${isEvenGroup ? 'justify-end' : 'justify-start'}`}
              >
                {productGroup.map((product, productIndex) => (
                  <div
                    key={`thumb-${product.id}-${productIndex}`}
                    className={`rounded-full border p-[3px] box-border cursor-pointer transition-all hover:border-white ${
                      currentProductIndex === productIndex ? 'border-white' : 'border-transparent'
                    }`}
                    onClick={() => {
                      handleProductSwitch(productIndex);
                    }}
                    // onMouseEnter={() => {
                    //   handleProductSwitch(productIndex);
                    // }}
                  >
                    <div className="w-[72px] h-[72px] rounded-full bg-[#eeeeee] flex items-center justify-center p-[7px] box-border">
                      <Image
                        src={product.image}
                        alt={`${product.short_name} Glasses`}
                        width={72}
                        height={72}
                        className="w-full h-auto mix-blend-multiply"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 商品详情 */}
            <div
              className={`mt-[24px] xl:mt-[32px] 2xl:mt-[59px] ${isEvenGroup ? 'text-right' : 'text-left'}`}
            >
              <h3 className="text-[48px] font-bb text-white mb-[5px] cursor-pointer">
                {currentProduct.short_name}
              </h3>
              <h4 className="text-[16px] text-white leading-[19px]">{currentProduct.name}</h4>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

PCProductGroupItem.displayName = 'PCProductGroupItem';

export default PCProductGroupItem;
